/**
 * Dialogue categories with icons and translations for all supported locales.
 */

import type { LocaleCode } from "@/lib/api/client";

export type CategoryId =
  | "food"
  | "transport"
  | "shopping"
  | "health"
  | "work"
  | "home"
  | "social"
  | "travel"
  | "services"
  | "education"
  | "other";

export interface CategoryInfo {
  id: CategoryId;
  icon: string; // Lucide icon name
  color: string; // Tailwind color class
}

const CATEGORIES: CategoryInfo[] = [
  { id: "food", icon: "UtensilsCrossed", color: "text-orange-500" },
  { id: "transport", icon: "Car", color: "text-blue-500" },
  { id: "shopping", icon: "ShoppingBag", color: "text-pink-500" },
  { id: "health", icon: "Heart", color: "text-red-500" },
  { id: "work", icon: "Briefcase", color: "text-slate-500" },
  { id: "home", icon: "Home", color: "text-green-500" },
  { id: "social", icon: "Users", color: "text-purple-500" },
  { id: "travel", icon: "Plane", color: "text-sky-500" },
  { id: "services", icon: "Wrench", color: "text-amber-500" },
  { id: "education", icon: "GraduationCap", color: "text-indigo-500" },
  { id: "other", icon: "MessageCircle", color: "text-gray-500" },
];

/**
 * Category translations for all supported locales.
 */
const CATEGORY_TRANSLATIONS: Record<string, Record<CategoryId, string>> = {
  // English
  "en-US": {
    food: "Food & Dining",
    transport: "Transport",
    shopping: "Shopping",
    health: "Health",
    work: "Work",
    home: "Home & Living",
    social: "Social",
    travel: "Travel",
    services: "Services",
    education: "Education",
    other: "Other",
  },
  "en-GB": {
    food: "Food & Dining",
    transport: "Transport",
    shopping: "Shopping",
    health: "Health",
    work: "Work",
    home: "Home & Living",
    social: "Social",
    travel: "Travel",
    services: "Services",
    education: "Education",
    other: "Other",
  },
  // Spanish
  "es-ES": {
    food: "Comida",
    transport: "Transporte",
    shopping: "Compras",
    health: "Salud",
    work: "Trabajo",
    home: "Hogar",
    social: "Social",
    travel: "Viajes",
    services: "Servicios",
    education: "Educación",
    other: "Otro",
  },
  "es-MX": {
    food: "Comida",
    transport: "Transporte",
    shopping: "Compras",
    health: "Salud",
    work: "Trabajo",
    home: "Hogar",
    social: "Social",
    travel: "Viajes",
    services: "Servicios",
    education: "Educación",
    other: "Otro",
  },
  // Portuguese
  "pt-PT": {
    food: "Alimentação",
    transport: "Transportes",
    shopping: "Compras",
    health: "Saúde",
    work: "Trabalho",
    home: "Casa",
    social: "Social",
    travel: "Viagens",
    services: "Serviços",
    education: "Educação",
    other: "Outro",
  },
  "pt-BR": {
    food: "Alimentação",
    transport: "Transporte",
    shopping: "Compras",
    health: "Saúde",
    work: "Trabalho",
    home: "Casa",
    social: "Social",
    travel: "Viagens",
    services: "Serviços",
    education: "Educação",
    other: "Outro",
  },
  // French
  "fr-FR": {
    food: "Restauration",
    transport: "Transport",
    shopping: "Shopping",
    health: "Santé",
    work: "Travail",
    home: "Maison",
    social: "Social",
    travel: "Voyages",
    services: "Services",
    education: "Éducation",
    other: "Autre",
  },
  // German
  "de-DE": {
    food: "Essen",
    transport: "Verkehr",
    shopping: "Einkaufen",
    health: "Gesundheit",
    work: "Arbeit",
    home: "Zuhause",
    social: "Soziales",
    travel: "Reisen",
    services: "Dienste",
    education: "Bildung",
    other: "Sonstiges",
  },
  // Italian
  "it-IT": {
    food: "Cibo",
    transport: "Trasporti",
    shopping: "Shopping",
    health: "Salute",
    work: "Lavoro",
    home: "Casa",
    social: "Sociale",
    travel: "Viaggi",
    services: "Servizi",
    education: "Istruzione",
    other: "Altro",
  },
  // Russian
  "ru-RU": {
    food: "Еда",
    transport: "Транспорт",
    shopping: "Покупки",
    health: "Здоровье",
    work: "Работа",
    home: "Дом",
    social: "Общение",
    travel: "Путешествия",
    services: "Услуги",
    education: "Образование",
    other: "Другое",
  },
  // Ukrainian
  "uk-UA": {
    food: "Їжа",
    transport: "Транспорт",
    shopping: "Покупки",
    health: "Здоров'я",
    work: "Робота",
    home: "Дім",
    social: "Спілкування",
    travel: "Подорожі",
    services: "Послуги",
    education: "Освіта",
    other: "Інше",
  },
  // Japanese
  "ja-JP": {
    food: "食事",
    transport: "交通",
    shopping: "買い物",
    health: "健康",
    work: "仕事",
    home: "家庭",
    social: "社交",
    travel: "旅行",
    services: "サービス",
    education: "教育",
    other: "その他",
  },
  // Korean
  "ko-KR": {
    food: "음식",
    transport: "교통",
    shopping: "쇼핑",
    health: "건강",
    work: "직장",
    home: "가정",
    social: "사교",
    travel: "여행",
    services: "서비스",
    education: "교육",
    other: "기타",
  },
  // Chinese
  "zh-CN": {
    food: "餐饮",
    transport: "交通",
    shopping: "购物",
    health: "健康",
    work: "工作",
    home: "家居",
    social: "社交",
    travel: "旅行",
    services: "服务",
    education: "教育",
    other: "其他",
  },
  // Polish
  "pl-PL": {
    food: "Jedzenie",
    transport: "Transport",
    shopping: "Zakupy",
    health: "Zdrowie",
    work: "Praca",
    home: "Dom",
    social: "Życie towarzyskie",
    travel: "Podróże",
    services: "Usługi",
    education: "Edukacja",
    other: "Inne",
  },
  // Hebrew
  "he-IL": {
    food: "אוכל",
    transport: "תחבורה",
    shopping: "קניות",
    health: "בריאות",
    work: "עבודה",
    home: "בית",
    social: "חברתי",
    travel: "טיולים",
    services: "שירותים",
    education: "חינוך",
    other: "אחר",
  },
  // Finnish
  "fi-FI": {
    food: "Ruoka",
    transport: "Liikenne",
    shopping: "Ostokset",
    health: "Terveys",
    work: "Työ",
    home: "Koti",
    social: "Sosiaaliset",
    travel: "Matkailu",
    services: "Palvelut",
    education: "Koulutus",
    other: "Muu",
  },
  // Estonian
  "et-EE": {
    food: "Toit",
    transport: "Transport",
    shopping: "Ostlemine",
    health: "Tervis",
    work: "Töö",
    home: "Kodu",
    social: "Sotsiaalne",
    travel: "Reisimine",
    services: "Teenused",
    education: "Haridus",
    other: "Muu",
  },
  // Latvian
  "lv-LV": {
    food: "Ēdiens",
    transport: "Transports",
    shopping: "Iepirkšanās",
    health: "Veselība",
    work: "Darbs",
    home: "Mājas",
    social: "Sociālais",
    travel: "Ceļojumi",
    services: "Pakalpojumi",
    education: "Izglītība",
    other: "Cits",
  },
};

/**
 * Get translated category name for a locale.
 * Falls back to English if locale not found.
 */
export function getCategoryName(categoryId: CategoryId, locale: LocaleCode): string {
  const translations = CATEGORY_TRANSLATIONS[locale] ?? CATEGORY_TRANSLATIONS["en-US"]!;
  return translations[categoryId] ?? translations.other;
}

/**
 * Get category info by ID.
 */
export function getCategoryInfo(categoryId: CategoryId): CategoryInfo {
  return CATEGORIES.find(c => c.id === categoryId) ?? CATEGORIES[CATEGORIES.length - 1]!;
}

